import { useCallback, useEffect, useState } from "react";
import { ArrowsClockwise, TrendUp, TrendDown, Minus, CurrencyBtc } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const oops = (e) => toast.error(formatApiErrorDetail(e?.response?.data?.detail));
const num = (v) => (v == null ? "—" : Number(v).toLocaleString(undefined, { maximumFractionDigits: 4 }));

const Move = ({ pct }) => {
  const Icon = pct == null ? Minus : pct >= 0 ? TrendUp : TrendDown;
  const tone = pct == null ? "text-slate-400" : pct >= 0 ? "text-[#0B5C2E]" : "text-[#B3241B]";
  return (
    <span className={`inline-flex items-center gap-1 text-sm font-semibold ${tone}`}>
      <Icon size={14} />{pct == null ? "—" : `${pct > 0 ? "+" : ""}${pct}%`}
    </span>
  );
};

export default function Commodities() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const canLink = ["admin", "vendor", "buyer"].includes(user?.role);
  const [view, setView] = useState("board");
  const [board, setBoard] = useState(null);
  const [insight, setInsight] = useState(null);
  const [hints, setHints] = useState(null);
  const [index, setIndex] = useState(null);
  const [group, setGroup] = useState("");
  const [coins, setCoins] = useState(null);
  const [items, setItems] = useState([]);
  const [link, setLink] = useState({ item_id: "", commodity: "" });
  const [busy, setBusy] = useState(false);
  const [priceFor, setPriceFor] = useState(null);
  const [priceValue, setPriceValue] = useState("");

  const loadBoard = useCallback(() => {
    api.get("/commodities/board").then(({ data }) => setBoard(data)).catch(() => setBoard(null));
  }, []);
  const loadStock = useCallback(() => {
    api.get("/commodities/items").then(({ data }) => setInsight(data)).catch(() => setInsight(null));
    api.get("/commodities/reorder-hints").then(({ data }) => setHints(data)).catch(() => setHints(null));
  }, []);
  const loadIndex = useCallback(() => {
    api.get(`/commodities/index${group ? `?group=${group}` : ""}`)
      .then(({ data }) => setIndex(data)).catch(() => setIndex(null));
  }, [group]);

  useEffect(loadBoard, [loadBoard]);
  useEffect(() => {
    if (view === "stock") loadStock();
    if (view === "index") loadIndex();
    if (view === "crypto") api.get("/commodities/crypto").then(({ data }) => setCoins(data)).catch(() => setCoins({ coins: [], unavailable: true }));
  }, [view, loadStock, loadIndex]);
  useEffect(() => {
    if (canLink) api.get("/items").then(({ data }) => setItems(data)).catch(() => setItems([]));
  }, [canLink]);

  const refresh = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/commodities/refresh", {});
      toast.success(data.note || "Refreshing prices in the background");
      setTimeout(() => { loadBoard(); setBusy(false); }, ((data.estimated_seconds || 60) + 10) * 1000);
      setTimeout(loadBoard, 30000);
    } catch (e) { oops(e); setBusy(false); }
  };

  const savePrice = async () => {
    const value = Number(priceValue);
    if (priceValue === "" || Number.isNaN(value) || value < 0) {
      toast.error("Give a price of zero or more");
      return;
    }
    try {
      await api.post(`/commodities/${priceFor.key}/quote`, { value });
      toast.success(`${priceFor.label} updated`);
      setPriceFor(null);
      loadBoard();
    } catch (e) { oops(e); }
  };

  const saveLink = async () => {
    try {
      await api.post("/commodities/links", link);
      toast.success("Stock item linked to its market");
      setLink({ item_id: "", commodity: "" });
      loadStock();
    } catch (e) { oops(e); }
  };

  const removeLink = async (itemId) => {
    try {
      await api.delete(`/commodities/links/${itemId}`);
      toast.success("Unlinked");
      loadStock();
    } catch (e) { oops(e); }
  };

  return (
    <div className="space-y-6" data-testid="commodities-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="overline text-[#B3241B]">Commodities</p>
          <h1 className="font-display text-3xl font-bold tracking-tight text-[#0F172A]">What your materials cost the world over</h1>
          <p className="mt-2 max-w-2xl text-sm text-slate-600">
            Live prices for oil, gas, copper, aluminium and cotton, our own price index for steel,
            plastics, fasteners, tools and cleaning supplies built from what you actually pay, and a
            nudge when the market makes it a good moment to restock.
          </p>
        </div>
        {isAdmin && (
          <Button variant="outline" data-testid="cmd-refresh" onClick={refresh} disabled={busy}>
            <ArrowsClockwise size={16} className="mr-1.5" /> {busy ? "Working…" : "Refresh prices"}
          </Button>
        )}
      </div>

      {board && !board.provider_ready && (
        <p data-testid="cmd-manual-note" className="border-l-2 border-[#002FA7] bg-[#EEF3FF] px-3 py-2 text-xs text-[#0F172A]">
          Live commodity prices are unavailable at the moment. Set the prices you know by hand;
          your own index from purchase orders works either way, and crypto is already live.
        </p>
      )}

      <Tabs value={view} onValueChange={setView} className="w-full scroll-x overflow-x-auto">
        <TabsList data-testid="cmd-tabs" className="w-max">
          <TabsTrigger value="board" data-testid="cmd-tab-board">Price board</TabsTrigger>
          <TabsTrigger value="stock" data-testid="cmd-tab-stock">Against our stock</TabsTrigger>
          <TabsTrigger value="index" data-testid="cmd-tab-index">Our price index</TabsTrigger>
          <TabsTrigger value="crypto" data-testid="cmd-tab-crypto">Crypto</TabsTrigger>
        </TabsList>
      </Tabs>

      {view === "board" && (
        !board ? <p className="py-10 text-center text-sm text-slate-500">Loading…</p> : (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3" data-testid="cmd-board">
            {board.commodities.map((row, i) => (
              <div key={row.key} data-testid={`cmd-card-${row.key}`} className="border border-border bg-white p-4">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <p className="font-semibold text-[#0F172A]">{row.label}</p>
                    <p className="mt-0.5 text-[11px] uppercase tracking-wide text-slate-500">
                      {row.group} · {row.unit} · {row.live ? "live feed" : "our buying"}
                    </p>
                  </div>
                  <Move pct={row.change_pct} />
                </div>
                <p className="mt-3 text-2xl font-bold text-[#0F172A]" data-testid={`cmd-value-${row.key}`}>{num(row.value)}</p>
                <p className="mt-2 text-[11px] text-slate-400">
                  {row.as_of ? `As of ${new Date(row.as_of).toLocaleString()}`
                    : row.own_index?.buys ? `Average paid across ${row.own_index.buys} purchase order line(s) for stock linked to this market`
                      : "No price yet"}
                </p>
                {isAdmin && (
                  <button data-testid={`cmd-set-${row.key}`} className="mt-3 text-xs font-semibold text-[#002FA7]"
                    onClick={() => { setPriceFor(row); setPriceValue(row.value ?? ""); }}>
                    Set the price
                  </button>
                )}
              </div>
            ))}
          </div>
        )
      )}

      {view === "stock" && (
        <div className="flex flex-col gap-4" data-testid="cmd-stock">
          {hints?.hints?.length > 0 && (
            <div className="order-1 border-l-2 border-[#B3241B] bg-[#FFF6E5] px-4 py-3" data-testid="cmd-hints">
              <p className="text-sm font-semibold text-[#0F172A]">Worth restocking now</p>
              <ul className="mt-1 space-y-1 text-sm text-slate-700">
                {hints.hints.map((h, i) => (
                  <li key={h.item_id} data-testid={`cmd-hint-${i}`}>
                    <span className="font-semibold">{h.item_name}</span> — {h.reason}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {canLink && (
            <div className="order-4 border border-border bg-white p-4 md:order-2">
              <p className="text-sm font-semibold text-[#0F172A]">Link a stock item to its market</p>
              <div className="mt-3 grid gap-3 sm:grid-cols-3">
                <div>
                  <Label>Stock item</Label>
                  <select data-testid="cmd-link-item" className="mt-1 w-full border border-border bg-white px-2 py-2 text-base md:text-sm" value={link.item_id} onChange={(e) => setLink({ ...link, item_id: e.target.value })}>
                    <option value="">Pick an item</option>
                    {items.map((i) => <option key={i.id} value={i.id}>{i.name}</option>)}
                  </select>
                </div>
                <div>
                  <Label>Market</Label>
                  <select data-testid="cmd-link-commodity" className="mt-1 w-full border border-border bg-white px-2 py-2 text-base md:text-sm" value={link.commodity} onChange={(e) => setLink({ ...link, commodity: e.target.value })}>
                    <option value="">Pick a market</option>
                    {(board?.commodities || []).map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
                  </select>
                </div>
                <div className="flex items-end">
                  <Button data-testid="cmd-link-submit" onClick={saveLink}>Link them</Button>
                </div>
              </div>
            </div>
          )}

          {!insight || insight.items.length === 0 ? (
            <p data-testid="cmd-stock-empty" className="order-3 border border-border bg-white p-8 text-center text-sm text-slate-600">
              No stock linked to a market yet — link an item above and you&apos;ll see how its market has moved since you last bought.
            </p>
          ) : (
            <>
            <div className="order-2 space-y-2 md:hidden" data-testid="cmd-stock-cards">
              {insight.items.map((row, i) => (
                <div key={row.item_id} data-testid={`cmd-stock-card-${i}`} className="border border-border bg-white p-4">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <p className="font-semibold text-[#0F172A]">{row.item_name}</p>
                      <p className="text-xs text-slate-500">{row.commodity_label}{row.category ? ` · ${row.category}` : ""}</p>
                    </div>
                    <Move pct={row.market_change_pct} />
                  </div>
                  <div className="mt-3 grid grid-cols-2 gap-2 text-xs text-slate-500">
                    <p>In stock: <span className="text-[#0F172A]">{row.quantity}{row.reorder_level > 0 && row.quantity <= row.reorder_level ? " ⚠" : ""}</span></p>
                    <p>Last paid: <span className="text-[#0F172A]">{row.last_paid != null ? num(row.last_paid) : "—"}</span></p>
                  </div>
                  {canLink && (
                    <button data-testid={`cmd-unlink-card-${i}`} className="tap-safe mt-3 text-xs font-semibold text-slate-500 hover:text-[#B3241B]" onClick={() => removeLink(row.item_id)}>Unlink</button>
                  )}
                </div>
              ))}
            </div>
            <div className="order-3 hidden overflow-x-auto border border-border bg-white md:order-3 md:block">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                  <tr>
                    <th className="p-3 text-left">Stock item</th><th className="p-3 text-left">Market</th>
                    <th className="p-3 text-right">In stock</th><th className="p-3 text-right">Last paid</th>
                    <th className="p-3 text-right">Market since</th><th className="p-3" />
                  </tr>
                </thead>
                <tbody data-testid="cmd-stock-rows">
                  {insight.items.map((row, i) => (
                    <tr key={row.item_id} className="border-t border-border" data-testid={`cmd-stock-row-${i}`}>
                      <td className="p-3">
                        <span className="font-semibold text-[#0F172A]">{row.item_name}</span>
                        <span className="block text-xs text-slate-500">{row.category}</span>
                      </td>
                      <td className="p-3 text-slate-600">{row.commodity_label}</td>
                      <td className="p-3 text-right">{row.quantity}{row.reorder_level > 0 && row.quantity <= row.reorder_level ? " ⚠" : ""}</td>
                      <td className="p-3 text-right">{row.last_paid != null ? num(row.last_paid) : "—"}</td>
                      <td className="p-3 text-right"><Move pct={row.market_change_pct} /></td>
                      <td className="p-3 text-right">
                        {canLink && (
                          <button data-testid={`cmd-unlink-${i}`} className="text-xs font-semibold text-slate-500 hover:text-[#B3241B]" onClick={() => removeLink(row.item_id)}>Unlink</button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            </>
          )}
        </div>
      )}

      {view === "index" && (
        <div className="space-y-4" data-testid="cmd-index">
          <div className="flex flex-wrap items-end gap-3">
            <div>
              <Label>Group</Label>
              <select data-testid="cmd-index-group" className="mt-1 border border-border bg-white px-3 py-2 text-base md:text-sm" value={group} onChange={(e) => setGroup(e.target.value)}>
                <option value="">Everything we buy</option>
                {(board?.groups || []).map((g) => <option key={g} value={g}>{g}</option>)}
              </select>
            </div>
            <p className="pb-2 text-sm text-slate-600">
              Built from {index?.buys ?? 0} purchase order line(s){index?.change_pct != null ? " · " : ""}
              {index?.change_pct != null && <Move pct={index.change_pct} />}
            </p>
          </div>
          {!index || index.points.length === 0 ? (
            <p data-testid="cmd-index-empty" className="border border-border bg-white p-8 text-center text-sm text-slate-600">
              Nothing to index yet — raise a few purchase orders and this fills in with what you are actually paying.
            </p>
          ) : (
            <div className="overflow-x-auto border border-border bg-white">
              <table className="w-full min-w-[420px] text-sm">
                <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                  <tr><th className="p-3 text-left">Month</th><th className="p-3 text-right">Average paid</th><th className="p-3 text-right">Buys</th></tr>
                </thead>
                <tbody data-testid="cmd-index-rows">
                  {index.points.map((p, i) => (
                    <tr key={p.month} className="border-t border-border" data-testid={`cmd-index-row-${i}`}>
                      <td className="p-3 font-semibold text-[#0F172A]">{p.month}</td>
                      <td className="p-3 text-right">{num(p.avg_price)}</td>
                      <td className="p-3 text-right">{p.buys}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {view === "crypto" && (
        <div className="space-y-3" data-testid="cmd-crypto">
          {coins?.unavailable && (
            <p data-testid="cmd-crypto-unavailable" className="border-l-2 border-[#B3241B] bg-[#FFECEB] px-3 py-2 text-xs text-[#7A1712]">
              {coins.note}
            </p>
          )}
          {coins?.stale && (
            <p data-testid="cmd-crypto-stale" className="border-l-2 border-[#002FA7] bg-[#EEF3FF] px-3 py-2 text-xs text-[#0F172A]">
              Showing the last prices we had.
            </p>
          )}
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3" data-testid="cmd-crypto-cards">
            {(coins?.coins || []).map((c) => (
              <div key={c.key} data-testid={`cmd-coin-${c.key}`} className="border border-border bg-white p-4">
                <div className="flex items-start justify-between gap-2">
                  <p className="font-semibold text-[#0F172A]"><CurrencyBtc size={15} className="mr-1 inline text-slate-400" />{c.label}</p>
                  <Move pct={c.change_pct} />
                </div>
                <p className="mt-3 text-2xl font-bold text-[#0F172A]">{num(c.value)} <span className="text-sm font-normal text-slate-500">{String(coins.vs).toUpperCase()}</span></p>
              </div>
            ))}
          </div>
          <p className="text-xs text-slate-500">Live from CoinGecko — no API key needed, refreshed every few minutes.</p>
        </div>
      )}

      <Dialog open={!!priceFor} onOpenChange={(o) => !o && setPriceFor(null)}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader><DialogTitle>{priceFor ? `Price for ${priceFor.label}` : "Set a price"}</DialogTitle></DialogHeader>
          {priceFor && (
            <div className="space-y-3">
              <div>
                <Label>Price ({priceFor.unit})</Label>
                <Input data-testid="cmd-price-input" type="number" min={0} value={priceValue}
                  onChange={(e) => setPriceValue(e.target.value)} placeholder="0.00" />
              </div>
              <Button className="w-full" data-testid="cmd-price-save" onClick={savePrice}>Save the price</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
